import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Voidwalker set (ADR-0160, mythic): a black star for a head, a suit cut out of the night sky, and the Singularity gun.
 *
 * The head is a hole in the picture: a sphere of black (a mask over the whole head) with a thin rim of violet light where
 * the garden's light bends round it, a few far stars in it, two eyes of white light edged in the team's colour, and a
 * ring round it like a planet's, tilted, white-hot at its middle and the team's colour at its rim: behind the sphere on
 * its far side, over it on the near one. The suit is the same black, rim-lit violet down its lit side, stars on it, the
 * team's light in a line at its collar and its belt. Its flair is the sky's: beads of light running round the head's
 * ring, stars coming and going in the black, two small moons circling the suit, motes of light falling into it from all
 * round, star dust kicked up on the run.
 *
 * Rubric (`assetc mythic` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead; the admin app's Skins
 * page, both previews; the `mythic-voidwalker` playtest: the Store, PLAY's stage, a round at 1920×1080): head S5 R5 P5
 * L4 C4 A4 F4, suit S3 R4 P5 L4 C4 F4. Still weak: on the night's dark ground the black leans on its violet rim and its
 * ring; the suit is a dark shape behind the gun (its collar's light and its moons carry it); lying, the ring crosses
 * the body.
 */

// --- The black star --------------------------------------------------------------------------------------------------

/** From the head's middle (u forward, v down): the sphere, and the ring round it (its middle, radii and tilt). */
const SPHERE: Ellipse = [0, 0, 19.5, 19.5];
const RING = { u: 0, v: 3.5, rx: 27, ry: 6.4, tilt: -0.2, wide: 0.1 } as const;
/** The eyes' middles and half-widths, near and far. */
const EYES: readonly (readonly [number, number, number])[] = [
  [-2.2, 1.2, 3.6],
  [10.2, 1.2, 2.7],
];

/** A star's place in the black by its pixel: one in ~45 lit, white or violet. */
function star(u: number, v: number): PaletteKey | null {
  const h = Math.sin(Math.floor(u) * 127.1 + Math.floor(v) * 311.7) * 43758.5453;
  const k = h - Math.floor(h);
  return k > 0.988 ? 'w' : k > 0.978 ? 'p' : null;
}

/** Where (u, v) is on the ring: its distance from the ring's line (0 on it, in the ring's own units) and its near side. */
function onRing(u: number, v: number): { off: number; near: boolean } {
  const [c, s] = [Math.cos(RING.tilt), Math.sin(RING.tilt)];
  const [du, dv] = [u - RING.u, v - RING.v];
  const [x, y] = [(du * c + dv * s) / RING.rx, (-du * s + dv * c) / RING.ry];
  return { off: Math.hypot(x, y) - 1, near: y > 0 };
}

const blackStar: HeadPicture = (u, v, lx, ly) => {
  const r = Math.sqrt(inEllipse(u, v, SPHERE));
  const ring = onRing(u, v);
  const lit = Math.abs(ring.off) < RING.wide;
  // The ring: white along its middle, the team's light to its rim; over the sphere only on its near side.
  if (lit && (ring.near || r > 1)) return Math.abs(ring.off) < 0.034 ? 'w' : ring.off > 0 ? 'b' : 'c';
  if (r > 1) return null;
  for (const [eu, ev, half] of EYES) {
    const du = (u - eu) / half;
    if (Math.abs(du) >= 1) continue;
    // An eye of light: a lens, its outer corner lifted; white, edged in the team's colour.
    const dv = (v - ev - du * (eu < 4 ? 0.9 : -0.9)) / 2.5;
    const e = du * du + dv * dv;
    if (e < 0.5) return 'w';
    if (e < 1) return 'c';
  }
  // Light bending round it: a violet rim on the lit side, brightest where it faces the light.
  const l = domeLight(u, v, SPHERE, lx, ly);
  if (r > 0.9) return l > 0.42 ? 'i' : l > 0.12 ? 'p' : l > -0.25 ? 'P' : '1';
  if (r > 0.8 && l > 0.5) return 'P';
  return star(u, v) ?? (l > 0.8 ? '1' : 'k');
};

// --- The starfield suit ----------------------------------------------------------------------------------------------

/**
 * The suit standing (the frame's x 11-48, y 43-55): the night's black, rim-lit violet along its lit edges, round
 * shoulders, stars on it, the team's light in a line round its collar and at its belt.
 */
function suit(): Part {
  const [x0, y0] = [11, 43];
  const c = new PixelCanvas(38, 13);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const body = (x: number, y: number) =>
    (x >= 16 && x <= 43 && y >= 44 && y <= 55) ||
    inEllipse(x + 0.5, y + 0.5, [14, 47.5, 4, 4]) <= 1 ||
    inEllipse(x + 0.5, y + 0.5, [45.5, 47.5, 3.6, 4]) <= 1;
  for (let y = 43; y <= 55; y++)
    for (let x = 10; x <= 49; x++) {
      if (!body(x, y)) continue;
      const edge = !body(x - 1, y) || !body(x, y - 1);
      at(x, y, edge ? (y < 50 ? 'p' : 'P') : !body(x - 2, y) || !body(x, y - 2) ? '1' : 'k');
    }
  // The collar's light: a line of the team's colour round the neck, down to a point.
  for (let k = 0; k <= 7; k++) {
    at(22 + k, 44 + Math.round(k * 0.7), 'c');
    at(38 - k, 44 + Math.round(k * 0.7), k < 3 ? 'c' : 'b');
  }
  for (const [x, y, k] of [
    [18, 47, 'w'],
    [21, 51, 'p'],
    [25, 47, 'p'],
    [27, 51, 'w'],
    [33, 48, 'i'],
    [36, 51, 'w'],
    [40, 47, 'p'],
    [13, 48, 'w'],
    [46, 49, 'p'],
  ] as const)
    at(x, y, k);
  for (let x = 16; x <= 43; x++) {
    at(x, 53, 'b');
    at(x, 54, 'c');
    at(x, 55, 'B');
  }
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): the belt's light at the waist (left), stars on the black. */
const DEAD_SUIT = grid(`
  ....pp11
  ...p11kk
  bp1kkkkk
  cPkkwkkk
  BPkkkkkk
  cPkkkkpk
  cPkkkkkk
  BPkpkkkk
  cPkkkkkk
  cPkkkkwk
  BPkkkkkk
  cPkkikkk
  cPkkkkkk
  BPkkkkkk
  cPkwkkk.
  cPkkkkkk
  b1kkkkkk
  ....kkkk
`);

/** Boots of the same black under the team's trousers. */
const STARFIELD = (): Outfit => ({ torso: suit(), dead: [DEAD_SUIT, 24, 51], legs: { n: '1', N: 'k' } });

// --- Singularity on every other gun ----------------------------------------------------------------------------------

/**
 * The Singularity finish (the default gun is its own, `pollinator_voidwalker`): black where the steel was, its glints
 * violet, violet to pink where the wood and the brass were.
 */
const SINGULARITY = {
  '1': 'k',
  '2': 'k',
  '3': '1',
  '4': '2',
  '5': 'p',
  N: 'k',
  n: '1',
  m: 'P',
  t: 'p',
  e: 'i',
  U: 'P',
  Y: 'p',
  y: 'i',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const PINK = 0xf6757a;
const MAGENTA = 0xb55088;
const VIOLET = 0x68386c;

const FLAIR: SetFlair = {
  head: [
    {
      kind: 'pulse',
      at: { on: 'head', x: 30, y: 28 },
      r: [23, 27],
      alpha: [0.1, 0.2],
      period: 2.2,
      tone: MAGENTA,
      layer: 'back',
    },
    // Beads of light running round the ring: behind the sphere on the far side, over it on the near one.
    {
      kind: 'orbit',
      at: { on: 'head', x: 30, y: 31.5 },
      rx: 27,
      ry: 6.4,
      tilt: -0.2,
      n: 9,
      period: 2.8,
      size: 2,
      tones: [WHITE, PINK, MAGENTA],
      tail: 0.11,
      rough: 0.08,
      glow: 4,
    },
    // Its eyes burn in the team's light.
    {
      kind: 'pulse',
      at: { on: 'head', x: 28, y: 29.5 },
      r: [4, 6.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'head', x: 40, y: 29.5 },
      r: [3.5, 5.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    // Far stars coming and going in the black.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 30, y: 27 },
      box: [32, 30],
      round: true,
      n: 9,
      period: [0.9, 2.2],
      tones: [WHITE, PINK, MAGENTA],
      layer: 'front',
    },
  ],
  outfit: [
    // Motes of light falling into it from all round.
    {
      kind: 'emit',
      at: { on: 'torso', x: 30, y: 46 },
      box: [84, 76],
      rate: 11,
      v: [-34, 34, -34, 34],
      carry: 1,
      pull: 260,
      drag: 0.35,
      life: [0.45, 0.8],
      size: [2, 1],
      tones: [VIOLET, MAGENTA, PINK, WHITE],
      alpha: [0.9, 1],
      add: true,
      layer: 'back',
    },
    // Two small moons circling the suit.
    {
      kind: 'orbit',
      at: { on: 'torso', x: 30, y: 51 },
      rx: 25,
      ry: 6.5,
      tilt: 0.16,
      n: 2,
      period: -2.6,
      size: 3,
      tones: [WHITE, PINK, MAGENTA, VIOLET],
      tail: 0.2,
      glow: 4,
    },
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 24, y: 49 },
      box: [18, 8],
      n: 5,
      period: [0.8, 1.9],
      tones: [WHITE, PINK],
      layer: 'front',
    },
    // Star dust on the run.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 68 },
      box: [12, 3],
      rate: 0,
      run: 0.07,
      v: [-6, 6, -22, -6],
      g: -6,
      drag: 0.4,
      life: [0.5, 0.95],
      size: [2, 1],
      tones: [WHITE, PINK, MAGENTA, VIOLET],
      add: true,
      glow: 4,
      layer: 'back',
    },
  ],
  gun: [
    // The star in the gun's cage: its light, and beads circling it.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 32, y: 9 },
      r: [7, 10],
      alpha: [0.2, 0.4],
      period: 1.3,
      tone: MAGENTA,
      layer: 'gun',
    },
    {
      kind: 'orbit',
      at: { on: 'gun', x: 32, y: 9.5 },
      rx: 9,
      ry: 3,
      tilt: -0.25,
      n: 4,
      period: 1.2,
      size: 2,
      tones: [WHITE, PINK, MAGENTA],
      tail: 0.3,
      layer: 'gun',
    },
  ],
};

/** The Voidwalker set (ADR-0160). */
export const VOIDWALKER = drawnLater({
  head: () => mask(blackStar),
  outfit: STARFIELD,
  gun: SINGULARITY,
  flair: FLAIR,
});
